// 캔버스 위의 객체 한 개.
//
// 좌표는 부모(문서 크기 div, transform: scale(zoom))의 px 그대로 쓴다 — 필드와
// 같은 규약. 글꼴은 PDF 와 같은 Pretendard 로 고정해 미리보기와 결과가 최대한
// 겹치게 한다(콘솔 기본 글꼴은 Wanted Sans).
//
// ── 표 선은 SVG 로 그린다 ─────────────────────────────────────────────────
// 처음엔 칸 div 의 CSS border 로 그렸는데 칸마다 두께가 다르면 이웃과 겹쳐 두 줄이
// 되고, 칸 위치도 두께만큼 밀렸다(오너 스크린샷 — 선이 어긋남). pdf-lib 처럼
// **획을 선 가운데에 놓는** SVG rect 로 바꿔 화면과 PDF 가 같은 방식으로 겹친다.

import {
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import {
  DEFAULT_CELL_IMAGE_PADDING,
  DEFAULT_LAYOUT_TEXT_ALIGN,
  layoutCellRect,
  layoutCellTextPadX,
  layoutCellTextPadY,
  layoutExpandRange,
  layoutLineEnds,
  layoutMasterOf,
  layoutTableEdges,
  type LayoutObject,
  type LayoutShape,
  type LayoutTable,
  type LayoutTableCell,
} from '@bhops/shared';
import { type CellRange, type Corner } from './useLayoutEditor';

const FONT = "'Pretendard Variable', Pretendard, sans-serif";

const FIELD_LABEL: Record<string, string> = {
  text: '텍스트',
  signature: '서명',
  stamp: '도장',
  date: '날짜',
  checkbox: '체크박스',
  dropdown: '드롭다운',
  image: '이미지',
};

/** 테두리 잡는 띠 두께(px) — 이보다 얇으면 잡기 어렵고, 두꺼우면 칸을 가린다. */
const EDGE = 6;
const HANDLE = 10;
/** 채우기 손잡이 한 변(px) — 엑셀처럼 작게, 그래도 잡을 만큼. */
const FILL_HANDLE = 7;

export interface LayoutObjectViewProps {
  obj: LayoutObject;
  selected: boolean;
  /** 선택 상자로 여럿을 함께 고른 것 중 하나 — 점선 테두리만(손잡이 없음). */
  grouped?: boolean;
  cellRange: CellRange | null;
  /** 채우기 손잡이를 끄는 동안 — 채워질 범위(원본 포함). */
  fillRange?: CellRange | null;
  editing: boolean;
  /** 사진 s3Key → URL. 없으면 자리만 그린다. */
  assetUrls: Record<string, string>;
  /** 사진이 안 열렸다(주소 만료 등) — 페이지가 주소를 새로 받는다. */
  onAssetError?: (s3Key: string) => void;
  /** 놓기 모드면 객체가 마우스를 먹지 않는다(빈 곳을 눌러 놓을 수 있게). */
  passThrough: boolean;
  onBodyMouseDown: (e: ReactMouseEvent) => void;
  onDoubleClick: () => void;
  onCellMouseDown: (e: ReactMouseEvent, r: number, c: number) => void;
  onCellMouseEnter: (r: number, c: number) => void;
  onCellText: (r: number, c: number, text: string) => void;
  onText: (text: string) => void;
  onStopEditing: () => void;
  onResizeMouseDown: (e: ReactMouseEvent, corner: Corner) => void;
  onColResizeMouseDown: (e: ReactMouseEvent, c: number) => void;
  onRowResizeMouseDown: (e: ReactMouseEvent, r: number) => void;
  onLineEndMouseDown: (e: ReactMouseEvent, end: 'a' | 'b') => void;
  /** 고른 범위 오른쪽 아래 채우기 손잡이(엑셀). */
  onFillHandleMouseDown?: (e: ReactMouseEvent) => void;
}

const handleSt: CSSProperties = {
  position: 'absolute',
  width: HANDLE,
  height: HANDLE,
  background: 'var(--bg-surface)',
  border: '1px solid var(--fg-brand)',
  boxSizing: 'border-box',
  zIndex: 32,
};

/** 네 모서리 크기 손잡이 + (표·텍스트·도형·사진 공용). */
function Corners({ o, on }: { o: LayoutObject; on: (e: ReactMouseEvent, c: Corner) => void }) {
  const defs: { c: Corner; x: number; y: number; cur: string }[] = [
    { c: 'nw', x: o.x, y: o.y, cur: 'nw-resize' },
    { c: 'ne', x: o.x + o.w, y: o.y, cur: 'ne-resize' },
    { c: 'sw', x: o.x, y: o.y + o.h, cur: 'sw-resize' },
    { c: 'se', x: o.x + o.w, y: o.y + o.h, cur: 'se-resize' },
  ];
  return (
    <>
      {defs.map((d) => (
        <div
          key={d.c}
          data-testid={`corner-${d.c}`}
          aria-label={`크기 조절 ${d.c}`}
          onMouseDown={(e) => on(e, d.c)}
          style={{ ...handleSt, left: d.x - HANDLE / 2, top: d.y - HANDLE / 2, cursor: d.cur }}
        />
      ))}
    </>
  );
}

/** 바깥 테두리 띠 — 잡으면 옮겨진다(오너: "표 테두리를 잡으면 표를 움직일 수 있게"). */
function EdgeBands({ o, on }: { o: LayoutObject; on: (e: ReactMouseEvent) => void }) {
  const bands: CSSProperties[] = [
    { left: o.x - EDGE / 2, top: o.y - EDGE / 2, width: o.w + EDGE, height: EDGE },
    { left: o.x - EDGE / 2, top: o.y + o.h - EDGE / 2, width: o.w + EDGE, height: EDGE },
    { left: o.x - EDGE / 2, top: o.y - EDGE / 2, width: EDGE, height: o.h + EDGE },
    { left: o.x + o.w - EDGE / 2, top: o.y - EDGE / 2, width: EDGE, height: o.h + EDGE },
  ];
  return (
    <>
      {bands.map((b, i) => (
        <div
          key={i}
          data-testid={i === 0 ? 'edge-band' : undefined}
          aria-label={i === 0 ? '표 옮기기' : undefined}
          onMouseDown={on}
          style={{ position: 'absolute', cursor: 'move', zIndex: 31, ...b }}
        />
      ))}
    </>
  );
}

export function LayoutObjectView(p: LayoutObjectViewProps) {
  const { obj, selected } = p;
  const frame: CSSProperties = {
    position: 'absolute',
    left: obj.x,
    top: obj.y,
    width: obj.w,
    height: obj.h,
    boxSizing: 'border-box',
    fontFamily: FONT,
    pointerEvents: p.passThrough ? 'none' : 'auto',
    // 사진은 늘 다른 객체 위(2026-09-23 오너 요청 — 표 위에 놓은 사진을 잡을 수 있게).
    // 입력 필드(11~)보다는 아래다.
    zIndex: obj.kind === 'image' ? 10 : selected || p.grouped ? 9 : 6,
    outline: selected
      ? '1px solid var(--fg-brand)'
      : p.grouped
        ? '1.5px dashed var(--fg-brand)'
        : undefined,
    outlineOffset: 1,
  };

  const isLine = obj.kind === 'shape' && obj.shape === 'line';

  let body: ReactNode;
  switch (obj.kind) {
    case 'table':
      body = <TableView t={obj} p={p} />;
      break;
    case 'text':
      body = (
        <div
          onMouseDown={p.onBodyMouseDown}
          onDoubleClick={p.onDoubleClick}
          style={{ ...frame, cursor: p.editing ? 'text' : 'move', overflow: 'hidden' }}
          data-testid="layout-text"
        >
          {p.editing ? (
            <textarea
              autoFocus
              aria-label="텍스트 내용"
              value={obj.text}
              onChange={(e) => p.onText(e.target.value)}
              onBlur={p.onStopEditing}
              onKeyDown={(e) => {
                if (e.key === 'Escape') p.onStopEditing();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                outline: 'none',
                resize: 'none',
                background: 'transparent',
                padding: 2,
                margin: 0,
                fontFamily: FONT,
                fontSize: obj.fontSize,
                fontWeight: obj.bold ? 700 : 400,
                textAlign: obj.align ?? DEFAULT_LAYOUT_TEXT_ALIGN,
                color: obj.color ?? '#222222',
                lineHeight: 1.35,
              }}
            />
          ) : (
            <div
              style={{
                padding: 2,
                fontSize: obj.fontSize,
                fontWeight: obj.bold ? 700 : 400,
                textAlign: obj.align ?? DEFAULT_LAYOUT_TEXT_ALIGN,
                color: obj.color ?? '#222222',
                lineHeight: 1.35,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                userSelect: 'none',
              }}
            >
              {obj.text || <span style={{ color: 'var(--text-secondary)' }}>텍스트</span>}
            </div>
          )}
        </div>
      );
      break;
    case 'shape':
      body = <ShapeView o={obj} p={p} frame={frame} />;
      break;
    case 'image':
      body = (
        <div
          onMouseDown={p.onBodyMouseDown}
          style={{
            ...frame,
            cursor: 'move',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: p.assetUrls[obj.s3Key] ? undefined : 'var(--bg-sunken)',
            border: p.assetUrls[obj.s3Key] ? undefined : '1px dashed var(--border-default)',
            overflow: 'hidden',
          }}
          data-testid="layout-image"
        >
          {p.assetUrls[obj.s3Key] ? (
            <img
              src={p.assetUrls[obj.s3Key]}
              alt={obj.fileName}
              draggable={false}
              onError={() => p.onAssetError?.(obj.s3Key)}
              style={{ width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' }}
            />
          ) : (
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {obj.fileName || '사진'}
            </span>
          )}
        </div>
      );
      break;
  }

  return (
    <>
      {body}
      {selected && !p.passThrough && !isLine && (
        <>
          {obj.kind === 'table' && <EdgeBands o={obj} on={p.onBodyMouseDown} />}
          <Corners o={obj} on={p.onResizeMouseDown} />
        </>
      )}
    </>
  );
}

// ── 도형 ──────────────────────────────────────────────────────────────────

function ShapeView({
  o,
  p,
  frame,
}: {
  o: LayoutShape;
  p: LayoutObjectViewProps;
  frame: CSSProperties;
}) {
  const s = o.stroke;
  const dash = s.style === 'dashed' ? '6 4' : undefined;
  const sw = Math.max(0, s.width);

  if (o.shape === 'line') {
    // 선은 상자 밖으로 획 절반이 나가고, 끝점 손잡이도 밖에 걸린다 — SVG 를 여유
    // 있게 잡고 overflow 를 열어 둔다(종전에 확대/축소에서 잘려 보이던 원인).
    const L = layoutLineEnds(o);
    const pad = Math.ceil(sw) + HANDLE;
    const left = o.x - pad;
    const top = o.y - pad;
    const W = o.w + pad * 2;
    const H = o.h + pad * 2;
    return (
      <>
        <svg
          data-testid="layout-line"
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          style={{
            position: 'absolute',
            left,
            top,
            overflow: 'visible',
            pointerEvents: p.passThrough ? 'none' : 'auto',
            zIndex: p.selected || p.grouped ? 9 : 6,
            filter: p.grouped ? 'drop-shadow(0 0 2px var(--fg-brand))' : undefined,
          }}
        >
          {/* 보이는 선 */}
          <line
            x1={L.x1 - left}
            y1={L.y1 - top}
            x2={L.x2 - left}
            y2={L.y2 - top}
            stroke={s.color}
            strokeWidth={Math.max(0.5, sw)}
            strokeDasharray={dash}
            strokeLinecap="butt"
          />
          {/* 잡는 선 — 얇은 선도 잡히게 두껍게, 보이지 않게 */}
          <line
            x1={L.x1 - left}
            y1={L.y1 - top}
            x2={L.x2 - left}
            y2={L.y2 - top}
            stroke="transparent"
            strokeWidth={Math.max(10, sw + 8)}
            style={{ cursor: 'move' }}
            onMouseDown={p.onBodyMouseDown}
          />
        </svg>
        {p.selected && !p.passThrough && (
          <>
            {(['a', 'b'] as const).map((end) => {
              const x = end === 'a' ? L.x1 : L.x2;
              const y = end === 'a' ? L.y1 : L.y2;
              return (
                <div
                  key={end}
                  data-testid={`line-end-${end}`}
                  aria-label={end === 'a' ? '선 시작점' : '선 끝점'}
                  onMouseDown={(e) => p.onLineEndMouseDown(e, end)}
                  style={{
                    ...handleSt,
                    borderRadius: '50%',
                    left: x - HANDLE / 2,
                    top: y - HANDLE / 2,
                    cursor: 'crosshair',
                  }}
                />
              );
            })}
          </>
        )}
      </>
    );
  }

  return (
    <div
      onMouseDown={p.onBodyMouseDown}
      style={{ ...frame, cursor: 'move' }}
      data-testid="layout-shape"
    >
      <svg width={o.w} height={o.h} style={{ display: 'block', overflow: 'visible' }}>
        {o.shape === 'rect' && (
          <rect
            x={sw / 2}
            y={sw / 2}
            width={Math.max(0, o.w - sw)}
            height={Math.max(0, o.h - sw)}
            fill={o.fill ?? 'none'}
            stroke={sw > 0 ? s.color : 'none'}
            strokeWidth={sw}
            strokeDasharray={dash}
          />
        )}
        {o.shape === 'ellipse' && (
          <ellipse
            cx={o.w / 2}
            cy={o.h / 2}
            rx={Math.max(0, o.w / 2 - sw / 2)}
            ry={Math.max(0, o.h / 2 - sw / 2)}
            fill={o.fill ?? 'none'}
            stroke={sw > 0 ? s.color : 'none'}
            strokeWidth={sw}
            strokeDasharray={dash}
          />
        )}
      </svg>
    </div>
  );
}

// ── 표 ────────────────────────────────────────────────────────────────────

function cellTextStyle(t: LayoutTable, cell: LayoutTableCell): CSSProperties {
  const v = cell.valign ?? 'middle';
  return {
    fontSize: cell.fontSize ?? t.fontSize,
    fontWeight: cell.bold ? 700 : 400,
    textAlign: cell.align ?? DEFAULT_LAYOUT_TEXT_ALIGN,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: v === 'top' ? 'flex-start' : v === 'bottom' ? 'flex-end' : 'center',
    lineHeight: 1.35,
    color: cell.color ?? '#222222',
  };
}

/**
 * 칸 글자 편집기 — 글자 높이만큼만 자라서 칸 안에서 위/가운데/아래로 선다.
 * textarea 는 스스로 세로 정렬을 못 해서, 종전에는 칸을 고르는 순간(=편집 중) 정렬이
 * 안 먹는 것처럼 보였다(2026-09-23 오너 지적).
 */
function CellEditorArea({
  value,
  placeholder,
  label,
  style,
  onChange,
}: {
  value: string;
  placeholder: string;
  label: string;
  style: CSSProperties;
  onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, style.fontSize]);
  return (
    <textarea
      ref={ref}
      autoFocus
      rows={1}
      aria-label={label}
      // 표 칸 복사·붙여넣기가 이 입력창을 알아본다(SignTemplateDesignerPage 의 copy/paste).
      data-cell-editor="1"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        ...style,
        display: 'block',
        width: '100%',
        maxHeight: '100%',
        border: 'none',
        outline: 'none',
        resize: 'none',
        background: 'transparent',
        padding: 0,
        margin: 0,
        overflow: 'hidden',
        fontFamily: FONT,
      }}
    />
  );
}

/**
 * 고른 범위를 그릴 자리(표 안 px). 병합까지 넓힌 사각형 + 시작 칸(병합이면 주인 칸 전체).
 * `single` 이면 칸 하나(병합 하나)만 고른 것 — 음영 없이 테두리만.
 */
export function selectionBox(
  t: LayoutTable,
  range: CellRange,
): {
  x: number;
  y: number;
  w: number;
  h: number;
  ax: number;
  ay: number;
  aw: number;
  ah: number;
  single: boolean;
} {
  const { r1, r2, c1, c2 } = layoutExpandRange(t, range.a, range.b);
  const sum = (xs: number[], from: number, to: number) =>
    xs.slice(from, to).reduce((a, b) => a + b, 0);
  const x = sum(t.colWidths, 0, c1);
  const y = sum(t.rowHeights, 0, r1);
  const w = sum(t.colWidths, c1, c2 + 1);
  const h = sum(t.rowHeights, r1, r2 + 1);
  const m = layoutMasterOf(t, range.a.r, range.a.c);
  const mc = t.cells[m.r][m.c];
  const mr2 = m.r + (mc.rowSpan ?? 1) - 1;
  const mc2 = m.c + (mc.colSpan ?? 1) - 1;
  const ax = sum(t.colWidths, 0, m.c);
  const ay = sum(t.rowHeights, 0, m.r);
  const aw = sum(t.colWidths, m.c, mc2 + 1);
  const ah = sum(t.rowHeights, m.r, mr2 + 1);
  const single = m.r === r1 && m.c === c1 && mr2 === r2 && mc2 === c2;
  return { x, y, w, h, ax, ay, aw, ah, single };
}

const OBJECT_POS_X: Record<string, string> = { left: 'left', center: 'center', right: 'right' };
const OBJECT_POS_Y: Record<string, string> = { top: 'top', middle: 'center', bottom: 'bottom' };

function TableView({ t, p }: { t: LayoutTable; p: LayoutObjectViewProps }) {
  const single =
    p.cellRange && p.cellRange.a.r === p.cellRange.b.r && p.cellRange.a.c === p.cellRange.b.c
      ? p.cellRange.a
      : null;
  const edges = layoutTableEdges(t);
  const sel = p.selected && p.cellRange ? selectionBox(t, p.cellRange) : null;
  const fill = p.selected && p.fillRange ? selectionBox(t, p.fillRange) : null;
  // 굵은 선이 표 밖으로 절반 나가므로 SVG 는 그만큼 넉넉히.
  const maxBw = edges.reduce((m, e) => Math.max(m, e.border.width), 0);
  const pad = Math.ceil(maxBw / 2) + 1;

  const cells: {
    r: number;
    c: number;
    cell: LayoutTableCell;
    rect: { x: number; y: number; w: number; h: number };
  }[] = [];
  for (let r = 0; r < t.cells.length; r++) {
    for (let c = 0; c < t.cells[r].length; c++) {
      const cell = t.cells[r][c];
      if (cell.covered) continue;
      const rect = layoutCellRect(t, r, c);
      cells.push({ r, c, cell, rect: { x: rect.x - t.x, y: rect.y - t.y, w: rect.w, h: rect.h } });
    }
  }

  // 열/행 경계 위치(표 안쪽만).
  const colX: number[] = [];
  let acc = 0;
  for (let c = 0; c < t.colWidths.length - 1; c++) {
    acc += t.colWidths[c];
    colX.push(acc);
  }
  const rowY: number[] = [];
  acc = 0;
  for (let r = 0; r < t.rowHeights.length - 1; r++) {
    acc += t.rowHeights[r];
    rowY.push(acc);
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: t.x,
        top: t.y,
        width: t.w,
        height: t.h,
        boxSizing: 'border-box',
        fontFamily: FONT,
        pointerEvents: p.passThrough ? 'none' : 'auto',
        zIndex: p.selected || p.grouped ? 9 : 6,
        outline: p.selected
          ? '1px solid var(--fg-brand)'
          : p.grouped
            ? '1.5px dashed var(--fg-brand)'
            : undefined,
        outlineOffset: 3,
      }}
      data-testid="layout-table"
    >
      {/* 음영(맨 아래) — 사진은 그 위, 선은 맨 위. PDF 도 같은 순서다. */}
      <svg
        width={t.w}
        height={t.h}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          overflow: 'visible',
          pointerEvents: 'none',
        }}
      >
        {cells.map(({ r, c, cell, rect }) =>
          cell.fill ? (
            <rect
              key={`f-${r}-${c}`}
              x={rect.x}
              y={rect.y}
              width={rect.w}
              height={rect.h}
              fill={cell.fill}
            />
          ) : null,
        )}
      </svg>

      {cells.map(({ r, c, cell, rect }) => {
        // 사진 칸은 글자를 찍지 않는다 — 입력창을 띄우면 사진을 가리고, 포커스가 거기 가서
        // Del 이 사진이 아니라 (보이지도 않는) 글자에 먹는다.
        const isEditing = p.selected && single && single.r === r && single.c === c && !cell.image;
        const img = cell.image?.s3Key ? p.assetUrls[cell.image.s3Key] : undefined;
        const padY = layoutCellTextPadY(rect.h, cell.fontSize ?? t.fontSize);
        const padX = layoutCellTextPadX(rect.w);
        const imgPad = Math.max(
          0,
          Math.min(
            cell.image?.padding ?? DEFAULT_CELL_IMAGE_PADDING,
            Math.min(rect.w, rect.h) / 2 - 1,
          ),
        );
        const fit = cell.image?.fit ?? 'contain';
        return (
          <div
            key={`${r}-${c}`}
            data-testid={`cell-${r}-${c}`}
            onMouseDown={(e) => p.onCellMouseDown(e, r, c)}
            onMouseEnter={() => p.onCellMouseEnter(r, c)}
            style={{
              position: 'absolute',
              left: rect.x,
              top: rect.y,
              width: rect.w,
              height: rect.h,
              boxSizing: 'border-box',
              overflow: 'hidden',
              cursor: 'cell',
            }}
          >
            {cell.image &&
              (img ? (
                <img
                  src={img}
                  alt={cell.image.fileName}
                  draggable={false}
                  onError={() => p.onAssetError?.(cell.image!.s3Key)}
                  style={{
                    position: 'absolute',
                    left: imgPad,
                    top: imgPad,
                    width: `calc(100% - ${imgPad * 2}px)`,
                    height: `calc(100% - ${imgPad * 2}px)`,
                    objectFit: fit === 'stretch' ? 'fill' : fit,
                    objectPosition: `${OBJECT_POS_X[cell.align ?? DEFAULT_LAYOUT_TEXT_ALIGN]} ${OBJECT_POS_Y[cell.valign ?? 'middle']}`,
                    opacity: cell.image.opacity ?? 1,
                    pointerEvents: 'none',
                  }}
                />
              ) : (
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    color: 'var(--text-secondary)',
                    pointerEvents: 'none',
                  }}
                >
                  {cell.image.fileName}
                </span>
              ))}
            {isEditing ? (
              <div
                style={{
                  ...cellTextStyle(t, cell),
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  padding: `${padY}px ${padX}px`,
                  boxSizing: 'border-box',
                }}
              >
                <CellEditorArea
                  label={`${r + 1}행 ${c + 1}열 글자`}
                  value={cell.text ?? ''}
                  placeholder={cell.field ? FIELD_LABEL[cell.field.type] : ''}
                  onChange={(v) => p.onCellText(r, c, v)}
                  style={{
                    fontSize: cell.fontSize ?? t.fontSize,
                    fontWeight: cell.bold ? 700 : 400,
                    textAlign: cell.align ?? DEFAULT_LAYOUT_TEXT_ALIGN,
                    lineHeight: 1.35,
                    color: cell.field ? 'var(--text-secondary)' : (cell.color ?? '#222222'),
                  }}
                />
              </div>
            ) : (
              !cell.image && (
                <div
                  style={{
                    ...cellTextStyle(t, cell),
                    width: '100%',
                    height: '100%',
                    padding: `${padY}px ${padX}px`,
                    boxSizing: 'border-box',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    userSelect: 'none',
                    ...(cell.field ? { color: 'var(--text-secondary)', fontStyle: 'italic' } : {}),
                  }}
                >
                  <span>{cell.text || (cell.field ? FIELD_LABEL[cell.field.type] : '')}</span>
                </div>
              )
            )}
          </div>
        );
      })}

      {/* 선 — 격자선 조각마다 한 번(PDF 와 같은 함수). 고른 칸 표시는 그 위. */}
      <svg
        width={t.w + pad * 2}
        height={t.h + pad * 2}
        viewBox={`${-pad} ${-pad} ${t.w + pad * 2} ${t.h + pad * 2}`}
        style={{
          position: 'absolute',
          left: -pad,
          top: -pad,
          overflow: 'visible',
          pointerEvents: 'none',
        }}
      >
        {edges.map((e, i) => (
          <line
            key={i}
            x1={e.x1}
            y1={e.y1}
            x2={e.x2}
            y2={e.y2}
            stroke={e.border.color}
            strokeWidth={e.border.width}
            strokeDasharray={e.border.style === 'dashed' ? '6 4' : undefined}
            strokeLinecap={e.border.style === 'dashed' ? 'butt' : 'square'}
          />
        ))}
        {/* 고른 범위 — 엑셀처럼 **한 사각형**(병합이 걸치면 그만큼 넓힌다). 음영 한 겹 +
            굵은 테두리 한 줄, 시작 칸은 음영 없이 비워 둔다(2026-09-23 오너 지적: 끌어 고르면
            칸마다 따로 칠해져 들쭉날쭉했다). */}
        {sel && (
          <g data-testid="cell-selection">
            {!sel.single && (
              <path
                data-testid="cell-selection-fill"
                fillRule="evenodd"
                fill="color-mix(in srgb, var(--fg-brand) 16%, transparent)"
                d={
                  `M${sel.x} ${sel.y}h${sel.w}v${sel.h}h${-sel.w}Z` +
                  `M${sel.ax} ${sel.ay}h${sel.aw}v${sel.ah}h${-sel.aw}Z`
                }
              />
            )}
            <rect
              data-testid="cell-selection-outline"
              x={sel.x}
              y={sel.y}
              width={sel.w}
              height={sel.h}
              fill="none"
              stroke="var(--fg-brand)"
              strokeWidth={2}
            />
          </g>
        )}
        {/* 채우기 손잡이를 끄는 동안 — 채워질 범위를 점선으로(엑셀과 같은 모양). */}
        {fill && (
          <rect
            data-testid="cell-fill-preview"
            x={fill.x}
            y={fill.y}
            width={fill.w}
            height={fill.h}
            fill="none"
            stroke="var(--fg-brand)"
            strokeWidth={1.5}
            strokeDasharray="4 3"
          />
        )}
      </svg>

      {/* 채우기 손잡이 — 고른 범위 오른쪽 아래 작은 네모. 끌면 숫자는 이어지는 번호, 글자는
          되풀이(2026-10-20 오너 요청, 규칙은 cell-fill.ts). */}
      {sel && !p.passThrough && p.onFillHandleMouseDown && (
        <div
          data-testid="cell-fill-handle"
          aria-label="채우기 손잡이"
          title="끌어서 이어 채웁니다. 숫자는 번호가 이어집니다."
          onMouseDown={p.onFillHandleMouseDown}
          style={{
            position: 'absolute',
            left: sel.x + sel.w - FILL_HANDLE / 2,
            top: sel.y + sel.h - FILL_HANDLE / 2,
            width: FILL_HANDLE,
            height: FILL_HANDLE,
            boxSizing: 'border-box',
            background: 'var(--fg-brand)',
            border: '1px solid var(--bg-surface)',
            cursor: 'crosshair',
            zIndex: 33,
          }}
        />
      )}

      {/* 열/행 경계 — 끌어서 그 열/행만 늘고 준다. 골라진 표에만. */}
      {p.selected &&
        !p.passThrough &&
        colX.map((x, c) => (
          <div
            key={`col-${c}`}
            data-testid={`col-handle-${c}`}
            aria-label={`${c + 1}열 너비 조절`}
            onMouseDown={(e) => p.onColResizeMouseDown(e, c)}
            style={{
              position: 'absolute',
              left: x - 3,
              top: 0,
              width: 6,
              height: t.h,
              cursor: 'col-resize',
              zIndex: 30,
            }}
          />
        ))}
      {p.selected &&
        !p.passThrough &&
        rowY.map((y, r) => (
          <div
            key={`row-${r}`}
            data-testid={`row-handle-${r}`}
            aria-label={`${r + 1}행 높이 조절`}
            onMouseDown={(e) => p.onRowResizeMouseDown(e, r)}
            style={{
              position: 'absolute',
              left: 0,
              top: y - 3,
              width: t.w,
              height: 6,
              cursor: 'row-resize',
              zIndex: 30,
            }}
          />
        ))}
    </div>
  );
}
